import type { FabricEditor } from 'modules/fabric-editor';
import {
  applyMatchLayout,
  authoredBundlesBbox,
  collectSurvivingBundles,
  MatchLayoutSettings,
} from 'modules/manager/domain/match-layout';
import type { ResolverContext } from 'modules/manager/domain/placeholder';

/**
 * Reposition the surviving match bundles inside the area the originally-
 * authored bundles 1..N occupied, per the template's `matchLayout` settings.
 * Runs after `filterMatchScopedObjects` (which removed out-of-range bundles)
 * and before `substitutePlaceholders` (so positions are computed against
 * authored placeholder sizes, not resolved-content sizes — matching what the
 * author saw in the editor).
 *
 * No-op when:
 *  - `settings` is disabled or absent
 *  - the authored DTO has no positively-`matchNumber`-tagged objects
 *  - no surviving bundles remain on the canvas
 *  - every authored bundle survived (M === N)
 */
export async function centerMatchBundles(
  editor: FabricEditor,
  ctx: Pick<ResolverContext, 'matches'>,
  settings: MatchLayoutSettings | undefined,
  authoredObjects: readonly unknown[] | undefined,
): Promise<void> {
  if (!settings || !settings.enabled) return;
  if (!authoredObjects || authoredObjects.length === 0) return;

  const authoredRegion = authoredBundlesBbox(authoredObjects);
  if (!authoredRegion) return;

  const authoredCount = countAuthoredMatches(authoredObjects);
  const survivingMatchCount = ctx.matches?.length ?? 0;
  if (survivingMatchCount === 0) return;
  if (survivingMatchCount >= authoredCount) return;

  const surviving = collectSurvivingBundles(editor.canvas.getObjects());
  if (surviving.length === 0) return;

  applyMatchLayout({ surviving, authoredRegion, settings });
  editor.canvas.requestRenderAll();
}

function countAuthoredMatches(authoredObjects: readonly unknown[]): number {
  let max = 0;
  for (const raw of authoredObjects) {
    const n = (raw as { data?: { matchNumber?: number } } | undefined)?.data?.matchNumber;
    if (typeof n === 'number' && n > max) max = n;
  }
  return max;
}
